import PropTypes from 'prop-types';
import React, { useState, useEffect, useCallback } from 'react';

import { debounce } from 'lodash'
import { Search } from 'lucide-react';
import { Col, Row, Input, Button, Separator, Label, Checkbox, cn } from 'twyne-new.ui';

const propTypes = {
  options: PropTypes.array,
  isSortable: PropTypes.bool,
  onChange: PropTypes.func,
  selectAllButton: PropTypes.bool,
};

export default function GroupCheck({ fields, options, onChange, isSortable, defaultValue, selectAllButton }) {
  const [groups, setGroups] = useState([])
  const [filteredGroups, setFilteredGroups] = useState([])
  const [selecteds, setSelecteds] = useState([])
  const [orders, setOrders] = useState({})
  const [selectedGroup, setSelectedGroup] = useState([])
  const [selectedAll, setSelectedAll] = useState(false)
  const [defaultMetrics, setDefaultMetrics] = useState([])
  const [searchTerm, setSearchTerm] = useState('')

  useEffect(() => {
    if (selectAllButton) {
      const optionValues = options.map(option => option.value)
      setDefaultMetrics(defaultValue.filter(value => !optionValues.includes(value)))
    }
  }, [])

  useEffect(() => {
    setSelecteds(getSelecteds())
  }, [defaultValue.length])

  const determineSelectedGroup = () => {
    if (!selectAllButton) return

    const groupsToUser = groups.length > 0 ? groups : getGroups()
    const selectedsToUser = selecteds.length > 0 ? selecteds : getSelecteds()

    const selectedAllParse = []
    for (const [title, options] of groupsToUser) {
      const alreadySelectedAll = options.every(option => selectedsToUser.includes(option.value))
      if (alreadySelectedAll) {
        selectedAllParse.push(title)
      }
    }

    setSelectedGroup([...selectedAllParse])
  }

  const determineSelectedAll = () => {
    if (!selectAllButton) return

    setSelectedAll((options.length + defaultMetrics.length) === selecteds.length)
  }

  const getGroups = () => {
    return Object.entries(options.reduce((groups, opt) => {
      groups[opt.group] = groups[opt.group] || []
      groups[opt.group].push(opt)
      return groups
    }, {}))
  }

  const getSelecteds = () => {
    return !isSortable ? defaultValue : defaultValue.map(valueOrder => {
      const [value, order] = valueOrder.split(' ')
      order && setOrders(orders => ({ ...orders, [value]: order }))
      return value
    })
  }

  useEffect(() => {
    const groupsOptions = getGroups()
    setGroups(groupsOptions)
    setFilteredGroups(groupsOptions)
    determineSelectedGroup()
    determineSelectedAll()
  }, [options])

  const handleClickOption = useCallback(option => checked => {
    if (checked) {
      selecteds.push(option)
    } else {
      const idx = selecteds.indexOf(option)
      selecteds.splice(idx, 1)
    }
    setSelecteds([...selecteds])
    onChange(selecteds.map(key => orders[key] ? `${key} ${orders[key]}` : key))
    determineSelectedGroup()
    determineSelectedAll()
  }, [orders, selecteds])

  const handleClickSort = useCallback(option => e => {
    e.stopPropagation()
    const prevValue = orders[option]
    orders[option] = prevValue === 'desc' ? 'asc' : 'desc'
    setOrders({ ...orders })
    onChange(selecteds.map(key => orders[key] ? `${key} ${orders[key]}` : key))
  }, [orders, selecteds])

  const handleClickSelectGroup = useCallback((options, title) => () => {
    const isSelected = selectedGroup.includes(title)
    if (!isSelected) {
      for (const option of options) {
        !selecteds.includes(option.value) && selecteds.push(option.value)
      }
      selectedGroup.push(title)
    } else {
      for (const option of options) {
        const idx = selecteds.indexOf(option.value)
        selecteds.splice(idx, 1)
      }

      const idx = selectedGroup.indexOf(title)
      selectedGroup.splice(idx, 1)
    }

    setSelecteds([...selecteds])
    setSelectedGroup([...selectedGroup])
    onChange(selecteds.map(key => orders[key] ? `${key} ${orders[key]}` : key))
    determineSelectedAll()
  }, [orders, selecteds])

  const handleClickSelectAll = () => {
    const isSelected = (options.length + defaultMetrics.length) === selecteds.length
    setSelectedAll(!isSelected)

    if (isSelected) {
      setSelecteds([defaultMetrics])
      setSelectedGroup([])
      onChange(defaultMetrics.map(key => orders[key] ? `${key} ${orders[key]}` : key))
    } else {
      const allOptions = [...options.map(item => item.value), ...defaultMetrics]
      setSelecteds(allOptions)
      setSelectedGroup(groups.map(([title]) => title))
      onChange(allOptions.map(key => orders[key] ? `${key} ${orders[key]}` : key))
    }
  }
  const applyFilter = value => debounce(() => {
    const regex = RegExp(value, 'i')
    const filtered = groups.map(([title, options]) => {
      const filteredOptions = options.filter(field => regex.test(field.label) || regex.test(field.value))

      if (!filteredOptions || !filteredOptions.length) {
        return null
      }

      return [title, filteredOptions]
    }).filter(value => !!value)

    setFilteredGroups(filtered)
  }, 400)()

  const handleChangeSearchTerm = value => {
    setSearchTerm(value)
    applyFilter(value)
  }

  return <>
    <SearchCard
      onChangeSearchTerm={handleChangeSearchTerm}
      searchTerm={searchTerm}
      selectAllButton={selectAllButton}
      onClickSelectAll={handleClickSelectAll}
      selectedAll={selectedAll}
    />
    <Separator className='my-3' />
    <div className={cn('max-h-[calc(100vh-25rem)] overflow-y-auto overflow-x-hidden', fields && 'max-h-[calc(100vh-30rem)]')}>
      {filteredGroups && filteredGroups.length ? filteredGroups.map(([title, options], gidx) => <Row key={gidx} className='pb-3'>
        <Col size='12'>
          <div className='flex items-center justify-between'>
            <span className='twy-text-base-accent text-md'>{title}</span>
            {selectAllButton && <>
              <Button size='sm' onClick={handleClickSelectGroup(options, title)} color='accent' variant='ghost'>
                {selectedGroup.includes(title) ? 'unselect all' : 'select all'}
              </Button>
            </>}
          </div>
        </Col>
        {options.map(({ label, value, disabled }, oidx) => {
          const checked = selecteds.includes(value)
          return <Col size="4" key={oidx} className='flex items-center gap-2 cursor-pointer' onClick={() => handleClickOption(value)(!checked)}>
            <Checkbox
              value={checked}
              disabled={disabled && disabled(selecteds)}
            />
            <Label>
              <span className='capitalize'>{label}</span>
              {isSortable && checked && <>
                <small onClick={e => handleClickSort(value)(e)} className='cursor-pointer text-sm twy-text-accent hover:underline'>{orders[value] || 'asc'}</small>
              </>}
            </Label>
          </Col>
        })}
        <Col size="12">
          <Separator />
        </Col>
      </Row>,
      ) : <div className="text-center p-4 twy-text-base-accent">Field not found</div>}
    </div>
  </>
}

function SearchCard({ onChangeSearchTerm, searchTerm, selectAllButton, onClickSelectAll, selectedAll }) {
  return <>
    <div className='relative w-full flex items-center justify-between'>
      <Input
        placeholder='Search...'
        className='pl-8 border-0 focus:bg-gray-200 focus:dark:bg-gray-950 z-0'
        value={searchTerm}
        onChange={e => onChangeSearchTerm(e.target.value)}
      />
      <Search size='18' className='absolute z-10 left-2 top-1/2 -translate-y-1/2' />
      {selectAllButton && <Button onClick={onClickSelectAll} variant='light' color='accent'>
        {selectedAll ? 'Unselect all' : 'Select all'}
      </Button>}
    </div>
  </>
}

GroupCheck.propTypes = propTypes
GroupCheck.defaultProps = {}
